import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Col, Container, Input, Row } from 'reactstrap';
import moment from 'moment';
import Loading from '../../../shared/components/custom/Loading';
import TooltipWithIcon from '../../../shared/components/custom/TooltipWithIcon';
import Api from '../../../util/api';
import t, { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import {
  TEAM_SLUG_COUNTRIES,
  TEAM_SLUG_SPORTS,
  TEAM_SLUG_PATTERN,
  TEAM_TYPE_PATTERN,
  buildTeamSlug,
  parseTeamSlug,
  slugifyTeamSlugPart,
} from './teamSlug';

// The update endpoint is a full PUT, not a patch - saving from this tab has
// to resend the plan's other fields exactly as last fetched, or they'd be
// wiped. See SubscriptionPlanFormPage's onSubscriptionPlanSubmit for the
// same payload shape built from the edit form instead.
const buildFullUpdatePayload = (plan, { teamSlug, teamType }) => ({
  name: plan.name,
  description: plan.description,
  isActive: plan.isActive || false,
  image: plan.imageUrl,
  subtitle: plan.subtitle,
  endDate: moment(plan.endDate),
  planDesiredValue: Number(plan.planDesiredValue) || 0,
  goalCapPerGame: plan.goalCapPerGame,
  showPriceMeter: plan.showPriceMeter || false,
  scoreType: plan.scoreType,
  externalInvoiceCompanies: plan.externalInvoiceCompanies || false,
  goalCapPerGameStep: Number(plan.goalCapPerGameStep) || 1,
  popularAmounts: plan.popularAmounts,
  amountStep: Number(plan.amountStep),
  amountMin: Number(plan.amountMin),
  teamSlug,
  teamType,
  defaultPaymentOption: {
    currency: plan.defaultPaymentOption.currency,
    optionName: t('paymentOptions.subscriptionPaymentForGoalSubscription', {
      entityName: plan.name,
    }),
    vat: Number(plan.defaultPaymentOption.vat),
  },
});

const TeamSlugPage = ({ planId }) => {
  const config = useConfig();
  const p = partial('goalSponsor');
  const s = partial('shared');
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [country, setCountry] = useState(
    config?.currencyCaps === 'NOK' ? 'no' : 'se'
  );
  const [team, setTeam] = useState('');
  const [sport, setSport] = useState('');
  const [town, setTown] = useState('');
  const [teamType, setTeamType] = useState('');

  const countryOptions = useMemo(() => TEAM_SLUG_COUNTRIES, []);
  const sportOptions = useMemo(
    () =>
      TEAM_SLUG_SPORTS.map((item) => ({
        value: slugifyTeamSlugPart(item),
        label: p(`teamSport_${item}`),
      })),
    []
  );

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const resPlan =
        await Api.goalSponsor.subscriptionPlans.getSubscriptionPlanById(planId);
      const defaultPaymentOption = await Api.transactions.getPaymentOption(
        resPlan.defaultPaymentOptionId
      );
      setPlan({ ...resPlan, defaultPaymentOption });
      const savedSlugParts = parseTeamSlug(resPlan.teamSlug);
      setCountry(
        savedSlugParts?.country ||
          (config?.currencyCaps === 'NOK' ? 'no' : 'se')
      );
      setTeam(savedSlugParts?.team || '');
      setSport(savedSlugParts?.sport || '');
      setTown(savedSlugParts?.town || '');
      setTeamType(resPlan.teamType || '');
    } catch (err) {
      toast.error(p('subscriptionPlansRetrievingERROR'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const teamSlugPreview = buildTeamSlug({ country, team, sport, town });
  const normalizedTeamType = teamType?.trim().normalize('NFC');

  const hasTeam = !!team?.trim();
  const hasSport = !!sport;
  const hasTown = !!town?.trim();
  let teamError = null;
  if ((hasSport || hasTown) && !hasTeam) {
    teamError = p('teamSlugTeamMissing');
  } else if (hasTeam && !slugifyTeamSlugPart(team)) {
    teamError = p('teamSlugInvalid');
  } else if (teamSlugPreview && !TEAM_SLUG_PATTERN.test(teamSlugPreview)) {
    teamError = p('teamSlugInvalid');
  }
  let townError = null;
  if (hasTown && !hasSport) {
    townError = p('teamSlugTownNeedsSport');
  } else if (hasTown && hasSport && !slugifyTeamSlugPart(town)) {
    townError = p('teamSlugInvalid');
  }
  const teamTypeError =
    normalizedTeamType && !TEAM_TYPE_PATTERN.test(normalizedTeamType)
      ? p('teamTypeInvalid')
      : null;
  const isValid = !teamError && !townError && !teamTypeError;

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildFullUpdatePayload(plan, {
        teamSlug: teamSlugPreview || null,
        teamType: normalizedTeamType || null,
      });
      await Api.goalSponsor.subscriptionPlans.updateSubscriptionPlan(
        planId,
        payload
      );
      toast.success(p('subscriptionPlanSaved'));
      fetchPlan();
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className="m-3">
      <Loading loading={loading || saving} />
      <Row>
        <Col md={4}>
          <div className="form__form-group">
            <span className="form-header">{p('teamSlugCountry')}</span>
            <Input
              type="select"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              {countryOptions.map((item) => (
                <option key={item} value={item}>
                  {p(`teamSlugCountry_${item}`)}
                </option>
              ))}
            </Input>
          </div>
        </Col>
        <Col md={8}>
          <div className="form__form-group">
            <span className="form-header">
              {p('teamSlugTeam')} &nbsp;
              <TooltipWithIcon
                id="team-slug-tooltip"
                text={p('teamSlugDescription')}
              />
            </span>
            <Input
              value={team}
              placeholder="Färjestad BK"
              onChange={(e) => setTeam(e.target.value)}
              invalid={!!teamError}
            />
            {teamError && (
              <span className="form__form-group-error">{teamError}</span>
            )}
          </div>
        </Col>
        <Col md={6}>
          <div className="form__form-group">
            <span className="form-header">
              {p('teamSlugSport')} ({s('optional')})
            </span>
            <Input
              type="select"
              value={sport}
              onChange={(e) => setSport(e.target.value)}
            >
              <option value="">{p('selectTeamSlugSport')}</option>
              {sportOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Input>
          </div>
        </Col>
        <Col md={6}>
          <div className="form__form-group">
            <span className="form-header">
              {p('teamSlugTown')} ({s('optional')})
            </span>
            <Input
              value={town}
              placeholder="Karlstad"
              onChange={(e) => setTown(e.target.value)}
              invalid={!!townError}
            />
            {townError && (
              <span className="form__form-group-error">{townError}</span>
            )}
          </div>
        </Col>
        <Col md={12}>
          <div className="form__form-group">
            <span className="form-header">{p('teamSlug')}</span>
            <div className="form__form-group-field">
              <input value={teamSlugPreview} readOnly disabled />
            </div>
          </div>
        </Col>
        <Col md={6}>
          <div className="form__form-group">
            <span className="form-header">
              {p('teamType')} &nbsp;
              <TooltipWithIcon
                id="team-type-tooltip"
                text={p('teamTypeDescription')}
              />
            </span>
            <Input
              value={teamType}
              placeholder="a-team, a-women, u21, p12"
              onChange={(e) => setTeamType(e.target.value.toLowerCase())}
              invalid={!!teamTypeError}
            />
            {teamTypeError && (
              <span className="form__form-group-error">{teamTypeError}</span>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button
            color="primary"
            size="sm"
            disabled={!isValid || !plan}
            onClick={save}
          >
            {s('save')}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default TeamSlugPage;
